import axios from "axios";
import config from '../config';
import {
  PilotSeminarSchema,
  ReferralOnboardingState,
  ReferralChannel,
  ReferralDirection,
  ReferralSchema,
  ReferralSource,
  ReferralStatsSchema,
  ReferralStatus,
} from '../data/types';

const axiosInstance = axios.create({
  baseURL: config.apiUrl,
  timeout: 100000,
  headers: {
    "Content-Type": "application/json",
    accept: "application/json",
  },
});

axiosInstance.defaults.headers.common['HOMECOOK-SECRET-KEY'] = process.env.REACT_APP_SECRET_KEY || "";
axiosInstance.defaults.headers.common['HOMECOOK-API-KEY'] = process.env.REACT_APP_API_KEY || "";

export type ReferralListFilters = {
  status?: ReferralStatus
  direction?: ReferralDirection
  source?: ReferralSource
  partner_slug?: string
  channel?: ReferralChannel
};

/** List tenant-bound referrals with server-side direction/source filtering. */
export const getCommunityReferrals = async (communityId: string, filters?: ReferralListFilters) => {
  const response = await axiosInstance.get(`/community/${communityId}/referrals`, { params: filters });
  return (response.data?.items || []) as ReferralSchema[];
};

/** Patch referral status / owner / next action. */
export const updateCommunityReferral = async (communityId: string, referralId: string, updates: Partial<ReferralSchema>) => {
  try {
    const response = await axiosInstance.patch(`/community/${communityId}/referrals/${referralId}`, updates);
    return response.data as ReferralSchema;
  } catch (error) {
    // const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    throw error;
  }
};

/** Remove a partner referral (and its funding booking) from the pipeline. */
export const deleteCommunityReferral = async (communityId: string, referralId: string) => {
  try {
    const response = await axiosInstance.delete(`/community/${communityId}/referrals/${referralId}`);
    return response.data as { id: string, deleted: boolean };
  } catch (error) {
    // const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    throw error;
  }
};

/** Update a public-safe onboarding state and enqueue its partner event. */
export const updatePartnerOnboardingState = async (
  communityId: string,
  referralId: string,
  onboardingState: ReferralOnboardingState,
) => {
  const response = await axiosInstance.patch(
    `/community/${communityId}/referrals/${referralId}/onboarding-status`,
    { onboarding_state: onboardingState },
  );
  return response.data as ReferralSchema;
};

/** Replay a queued or failed partner event. */
export const replayPartnerEvent = async (communityId: string, slug: string, eventId: string) => {
  const response = await axiosInstance.post(`/community/${communityId}/integrations/partners/${slug}/events/${eventId}/replay`);
  return response.data;
};

/** HomeCook ops referral list. */
export const getAdminReferrals = async (
  filters?: ReferralListFilters & { community_id?: string }
) => {
  const response = await axiosInstance.get('/admin/referrals', { params: filters });
  return (response.data?.items || []) as ReferralSchema[];
};

/** Day-90 scorecard aggregates. */
export const getAdminReferralStats = async (filters?: {
  community_id?: string
  resource_id?: string
  partner_slug?: string
  since?: string
}) => {
  const response = await axiosInstance.get('/admin/referrals/stats', { params: filters });
  return response.data as ReferralStatsSchema;
};

export type PipelineCampaignAudiencePreview = {
  total: number
  audience_label?: string
  sample_leads?: Array<{ id?: string; first_name?: string; business_name?: string }>
};

/** Preview Partner referrals who can receive a pipeline campaign. */
export const previewPipelineCampaign = async (
  communityId: string,
  resourceId: string,
  partnerSlug?: string,
) => {
  const response = await axiosInstance.post(
    `/community/${communityId}/resource/${resourceId}/pipeline-campaigns/preview`,
    { partner_slug: partnerSlug },
  );
  return response.data as PipelineCampaignAudiencePreview;
};

/** Send a campaign to this resource's pipeline members with emails. */
export const sendPipelineCampaign = async (
  communityId: string,
  resourceId: string,
  body: {
    name?: string
    subject: string
    message_html: string
    partner_slug?: string
  },
) => {
  const response = await axiosInstance.post(
    `/community/${communityId}/resource/${resourceId}/pipeline-campaigns`,
    body,
  );
  return response.data;
};

/** List joint-seminar registry records. */
/** List joint-seminar registry records. */
export const getPilotSeminars = async (filters?: {
  community_id?: string
  resource_id?: string
  partner_slug?: string
}) => {
  const response = await axiosInstance.get('/admin/pilot-seminars', { params: filters });
  return (response.data?.items || []) as PilotSeminarSchema[];
};

/** Create a joint-seminar registry record. */
export const createPilotSeminar = async (body: Partial<PilotSeminarSchema>) => {
  try {
    const response = await axiosInstance.post('/admin/pilot-seminars', body);
    return response.data as PilotSeminarSchema;
  } catch (error) {
    // const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    throw error;
  }
};

/** Remove a joint-seminar registry record. */
export const deletePilotSeminar = async (seminarId: string) => {
  const response = await axiosInstance.delete(`/admin/pilot-seminars/${seminarId}`);
  return response.data as { id: string, deleted: boolean };
};
